<template>
  <section class="invoice-preview-wrapper">
    <!-- Modal de Flujo de Estados -->
    <b-modal
      id="modal-status-flow"
      title="Flujo de Estados en un Pedido"
      ok-only
      ok-title="Entendido"
      ok-variant="primary"
      centered
      size="lg"
    >
      <div class="status-flow-diagram">
        <!-- Estado inicial -->
        <div class="status-row">
          <div class="status-box info">
            Pendiente
            <small class="d-block text-muted">Estado inicial</small>
          </div>
        </div>

        <div class="status-row mt-2">
          <div
            class="arrow"
            style="transform: rotate(90deg)"
          >
            →
          </div>
        </div>

        <!-- Posibles estados después de Pendiente -->
        <div class="status-row">
          <div class="status-box warning">
            Correcciones
            <small class="d-block text-muted">Requiere cambios</small>
          </div>
          <div class="arrow">
            ←
          </div>
          <div class="status-box success">
            Autorizado
            <small class="d-block text-muted">Aprobado por administración</small>
          </div>
          <div class="arrow">
            →
          </div>
          <div class="status-box danger">
            Rechazado/Anulado
            <small class="d-block text-muted">No procede</small>
          </div>
        </div>

        <!-- Flecha de retorno a Pendiente desde Correcciones -->
        <div class="status-row">
          <div
            class="arrow"
            style="transform: rotate(90deg)"
          >
            →
          </div>
        </div>

        <!-- Estados finales -->
        <div class="status-row">
          <div class="status-box secondary">
            Despachado
            <small class="d-block text-muted">Enviado al cliente</small>
          </div>
        </div>

        <div class="status-row mt-2">
          <div
            class="arrow"
            style="transform: rotate(90deg)"
          >
            →
          </div>
        </div>

        <div class="status-row">
          <div class="status-box dark">
            Facturado
            <small class="d-block text-muted">Proceso completado</small>
          </div>
        </div>

        <div class="mt-3 text-muted">
          <small>
            <feather-icon
              icon="InfoIcon"
              size="18"
              class="cursor-pointer mr-1"
            />
            El pedido puede ser anulado por el vendedor mientras esté pendiente, o rechazado por administración mientras esté pendiente o autorizado
          </small>
        </div>
      </div>
    </b-modal>

    <b-row class="invoice-add">
      <!-- Col: Left (Customer Order Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          class="invoice-preview-card"
          no-body
        >
          <b-card-body>
            <!-- Estado del pedido -->
            <b-row>
              <b-col
                cols="12"
                md="11"
              >
                <h3>
                  <b-badge
                    :variant="getStatusVariant(customerOrderData.Estado)"
                    pill
                  >
                    #{{ customerOrderData.Id }} - {{ customerOrderData.Estado }}
                  </b-badge>
                  <feather-icon
                    v-b-tooltip.hover
                    icon="HelpCircleIcon"
                    size="18"
                    class="cursor-pointer ml-1"
                    title="Ver flujo de estados"
                    @click="$bvModal.show('modal-status-flow')"
                  />
                </h3>
                <span v-if="customerOrderData.Cotizacion">
                  <b-link
                    style="font-size: 1.05rem; margin-left: 4px;"
                    @click="$router.push({ name: 'sales-customer-quotes', params: { id: customerOrderData.Cotizacion } })"
                  >
                    <u>Desde cotización #{{ customerOrderData.Cotizacion }}</u>
                  </b-link>
                </span>
                <p
                  v-if="customerOrderData.VentaCaida"
                  class="text-warning font-weight-bold mb-0 mt-50"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    class="mr-25"
                  />
                  Este pedido atendió {{ customerOrderData.VentaCaida.atendido_total }} de {{ customerOrderData.VentaCaida.solicitado_total }} unidades solicitadas en la cotización.
                  Venta caída: {{ customerOrderData.VentaCaida.unidades }} unidades<span v-if="customerOrderData.VentaCaida.monto"> ({{ getCurrencySymbol }}.{{ customerOrderData.VentaCaida.monto }})</span>.
                </p>
              </b-col>
            </b-row>

            <b-row class="mt-1">
              <b-col
                cols="12"
                xl="6"
                md="6"
              >
                <p
                  style="font-size: 13.5px;"
                >
                  <span class="font-weight-bold">Colocado por:</span>
                  {{ customerOrderData.UsuarioNombre }} <b>el:</b> {{ customerOrderData.FechaIngresoSistema }}
                  <br>
                  <span class="font-weight-bold">Fecha del pedido:</span>
                  {{ customerOrderData.FechaPedidoCliente }}
                </p>
              </b-col>
            </b-row>

            <!-- Info de cliente -->
            <b-row class="mt-1">
              <b-col
                cols="12"
                xl="12"
                md="12"
              >
                <h3>#{{ customerOrderData.CustomerInfo.id }} - {{ customerOrderData.CustomerInfo.name }}</h3>
              </b-col>
            </b-row>

            <!-- Info de encabezado -->
            <b-row>
              <b-col
                cols="12"
                xl="6"
                md="6"
              >
                <!-- Info del cliente -->
                <p>
                  <!-- NIT -->
                  <span class="font-weight-bold">NIT:</span>
                  {{ (customerOrderData.CustomerInfo.nit_cf === 'Y') ? `Consumidor final - ${customerOrderData.CustomerInfo.nit}` : customerOrderData.CustomerInfo.nit }}
                  <br>
                  <!-- Celular -->
                  <span class="font-weight-bold">Celular:</span>
                  {{ (customerOrderData.CustomerInfo.cellphone) ? customerOrderData.CustomerInfo.cellphone : '' }}
                  <br>
                  <!-- Teléfono -->
                  <span class="font-weight-bold">Teléfono:</span>
                  {{ (customerOrderData.CustomerInfo.phone) ? customerOrderData.CustomerInfo.phone : '' }}
                  <br>
                  <!-- Lista de precios -->
                  <span class="font-weight-bold">Lista de precios:</span>
                  {{ (customerOrderData.PriceListInfo && customerOrderData.PriceListInfo.name) ? customerOrderData.PriceListInfo.name : 'No asignada' }}
                  <br>
                </p>
              </b-col>
              <b-col
                cols="12"
                xl="6"
                md="6"
              >
                <!-- Dirección -->
                <p>
                  <span class="font-weight-bold">Dirección:</span>
                  {{ customerOrderData.DireccionEnvio }}
                  <br>
                </p>
              </b-col>
            </b-row>

            <!-- Tipo de venta -->
            <p>
              <span class="font-weight-bold">Tipo de venta:</span>
              {{ (customerOrderData.OriginSale) ? customerOrderData.OriginSale : 'Cliente Final' }}
            </p>

            <!-- Observaciones -->
            <p>
              <span class="font-weight-bold">Observaciones:</span>
              {{ (customerOrderData.Observaciones) ? customerOrderData.Observaciones : 'No hay observaciones' }}
            </p>
          </b-card-body>

          <!-- Detalle del pedido -->
          <b-card-body class="invoice-padding form-item-section">
            <b-table
              responsive
              :items="customerOrderData.Detalle"
              :fields="CustomerOrderFields"
            >
              <template #cell(producto_imagen)="data">
                <img
                  :src="getImageUrl(data.item.producto_imagen ? data.item.producto_imagen.path : null)"
                  :alt="data.item.producto_imagen ? data.item.producto_imagen.alt_text : ''"
                  class="img-fluid cursor-pointer"
                  style="width: 60px; height: 60px; border-color: #d8dee9; border-width: 1px; border-style: solid;"
                  @error="handleImageError"
                  @click="data.item.producto_imagen && ($bvModal.show('modal-image'), selectedImage = data.item.producto_imagen)"
                >
              </template>
              <template #cell(precio_pedido)="data">
                {{ getCurrencySymbol }}.{{ data.item.precio_pedido }}
              </template>
              <template #cell(total)="data">
                {{ getCurrencySymbol }}.{{ data.item.total }}
              </template>
            </b-table>
            <div
              v-if="customerOrderData.SummaryInfo[0]"
              class="float-right mr-4"
            >
              <p
                v-if="totalUnidades > 0"
                class="mb-1"
              >
                <span class="font-weight-bold">({{ totalUnidades }} Unidades)</span>
              </p>
              Total:
              <h4 _class="sale-total-amount">
                {{ getCurrencySymbol }}.{{ customerOrderData.SummaryInfo[0].total }}
              </h4>
            </div>
          </b-card-body>
        </b-card>

        <b-card
          class="invoice-preview-card"
          no-body
        >
          <b-card-body>
            <!-- Estado del pedido -->
            <b-row>
              <b-col
                cols="12"
                md="12"
              >
                <!-- Textarea para agregar comentarios -->
                <b-form-group
                  class="mb-0"
                >
                  <b-form-textarea
                    id="commentText"
                    v-model="commentText"
                    placeholder="Agregar comentario"
                  />

                  <!-- boton alineado a la derecha -->
                  <b-button
                    variant="outline-primary"
                    :disabled="commentText.length === 0 || disabledAction === 'disabled'"
                    class="float-right mt-1"
                    @click="addComment"
                  >
                    Comentar
                  </b-button>
                </b-form-group>

                <h3
                  v-if="(customerOrderData.Comentarios.length > 0)"
                  class="font-weight-bold mt-1"
                >
                  {{ customerOrderData.Comentarios.length }} Comentarios:
                </h3>

                <div
                  v-for="(comment, index) in customerOrderData.Comentarios"
                  :key="index"
                  class="mt-1"
                >
                  <!-- Si el comentario es tipo = user -->
                  <p v-if="comment.Tipo === 'Usuario'">
                    <span class="font-weight-bold">
                      {{ comment.UsuarioNombre }}
                    </span>
                    <span
                      v-b-tooltip.hover
                      class="float-right font-italic"
                      :title="comment.FechaExacta"
                    >
                      {{ comment.FechaRelativa }}
                    </span>
                    <br>
                    {{ comment.Comentario }}
                  </p>

                  <p v-if="comment.Tipo === 'Sistema'">
                    <span class="text-info">
                      <b>{{ comment.UsuarioNombre }}</b> realizó una acción:
                    </span>
                    <span
                      v-b-tooltip.hover
                      class="text-info float-right font-italic"
                      :title="comment.FechaExacta"
                    >
                      {{ comment.FechaRelativa }}
                    </span>
                    <br>
                    <span class="text-info">
                      {{ comment.Comentario }}
                    </span>
                  </p>

                  <!-- Spacer -->
                  <hr class="invoice-spacing">
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Col: Right (Actions Container) -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions mt-md-0 mt-2"
      >
        <!-- Action Buttons -->
        <b-card>
          <!-- Pencil feather icon -->
          <feather-icon
            v-if="customerOrderData.Estado === 'Pendiente' || customerOrderData.Estado === 'Correcciones'"
            v-b-tooltip.hover
            icon="Edit2Icon"
            size="26"
            class="cursor-pointer mr-1 mb-1"
            :title="getEditButtonTooltip(customerOrderData.Estado)"
            @click="$emit('addEditTab')"
          />
          <!-- Button: Mostrar PDF del pedido (solo si está autorizado) -->
          <feather-icon
            v-if="customerOrderData.Estado === 'Autorizado' || customerOrderData.Estado === 'Despachado' || customerOrderData.Estado === 'Facturado'"
            v-b-tooltip.hover
            icon="PrinterIcon"
            size="26"
            class="cursor-pointer mb-1"
            title="Generar PDF del pedido"
            @click="generarPDF()"
          />

          <div
            v-if="customerOrderData.Estado === 'Pendiente' ||
              (
                userData().role === 'admin' &&
                (customerOrderData.Estado === 'Autorizado' || customerOrderData.Estado === 'Despachado' || customerOrderData.Estado === 'Facturado')
              )"
          >
            <b-button
              v-if="customerOrderData.Estado === 'Pendiente' && userData().role === 'admin'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-success"
              block
              @click="changeStatus('Autorizado')"
            >
              Autorizar
            </b-button>

            <b-button
              v-if="customerOrderData.Estado === 'Pendiente' && userData().role === 'admin'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-warning"
              block
              @click="changeStatus('Correcciones')"
            >
              Solicitar correcciones
            </b-button>

            <b-button
              v-if="(customerOrderData.Estado === 'Pendiente' || customerOrderData.Estado === 'Autorizado') && userData().role === 'admin'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-danger"
              block
              @click="changeStatus('Rechazado')"
            >
              Rechazar
            </b-button>

            <b-button
              v-if="customerOrderData.Estado === 'Pendiente' && userData().role === 'vendor'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-danger"
              block
              @click="changeStatus('Anulado')"
            >
              Anular
            </b-button>

            <b-button
              v-if="customerOrderData.Estado === 'Autorizado' && userData().role === 'admin'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-secondary"
              block
              @click="changeStatus('Despachado')"
            >
              Marcar como despachado
            </b-button>

            <!-- Si está despachado puede marcarlo como Facturado -->
            <b-button
              v-if="customerOrderData.Estado === 'Despachado' && userData().role === 'admin'"
              v-ripple.400="'rgba(255, 159, 67, 0.15)'"
              variant="outline-dark"
              block
              @click="changeStatus('Facturado')"
            >
              Marcar como facturado
            </b-button>

            <!-- Si está facturado puede ver el listado de las facturas -->
            <div
              v-if="customerOrderData.Estado === 'Facturado' && userData().role === 'admin'"
            >
              <!-- Editar -->
              <feather-icon
                icon="EditIcon"
                size="20"
                class="cursor-pointer float-right"
                @click="editInvoices"
              />
              <p class="mt-1">
                Pedido facturado con las siguientes facturas:
              </p>
              <b-table
                class="position-relative"
                hover
                responsive
                small
                :items="customerOrderData.Facturas"
                :fields="[
                  { key: 'factura', label: 'Factura' },
                  { key: 'fecha', label: 'Ingresada' },
                ]"
              >
                <template #cell(factura)="data">
                  {{ data.item.factura }}
                </template>
                <template #cell(fecha)="data">
                  {{ data.item.fecha }}
                </template>
              </b-table>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      id="factura-modal"
      title="Ingresar factura(s)"
      cancel-title="Cancelar"
      ok-title="Guardar"
      centered
      hide-header-close
      @ok.prevent="saveInvoices"
      @hidden="invoiceNumber = ''"
    >
      <b-form-group>
        <b-input-group>
          <b-form-input
            v-model="invoiceNumber"
            placeholder="Ingrese el número de factura"
          />
          <b-input-group-append>
            <b-button
              variant="secondary"
              size="sm"
              @click="addInvoiceItem(invoiceNumber)"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
              />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <!-- Lista de facturas -->
      <b-list-group>
        <b-list-group-item
          v-for="(item, index) in invoiceItems"
          :key="index"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span>{{ item }}</span>
            <feather-icon
              icon="TrashIcon"
              size="16"
              @click="invoiceItems.splice(index, 1)"
            />
          </div>
        </b-list-group-item>
      </b-list-group>
    </b-modal>

    <!-- Modal para mostrar imagen en grande -->
    <b-modal
      id="modal-image"
      hide-footer
      centered
      size="lg"
      :title="selectedImage.alt_text"
    >
      <div class="text-center">
        <img
          :src="getImageUrl(selectedImage.path)"
          :alt="selectedImage.alt_text"
          class="img-fluid"
          @error="handleImageError"
        >
      </div>
    </b-modal>
  </section>
</template>

<script>
import axios from '@axios'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import Ripple from 'vue-ripple-directive'
import OrderGenerator from '@/services/pdf/orderGenerator'

export default {
  directives: { Ripple },

  props: {
    orderId: {
      type: Number,
      default: () => 0,
    },
  },

  data() {
    return {
      customerOrderData: {
        Id: '',
        Estado: '',
        FechaPedidoCliente: '',
        FechaIngresoSistema: '',
        UsuarioNombre: '',
        SummaryInfo: [],
        Detalle: [],
        Comentarios: [],
        Observaciones: '',
        Vendedor: '',
        VendedorEmail: '',
        CustomerInfo: [],
        Cotizacion: '',
        VentaCaida: null,
        PriceListInfo: null,
        CurrencyInfo: null,
      },

      CustomerOrderFields: [
        { key: 'producto_imagen', label: 'Imagen' },
        { key: 'sku', label: 'Producto' },
        { key: 'producto_desc', label: 'Descripción' },
        { key: 'cantidad', label: 'Cantidad' },
        { key: 'precio_pedido', label: 'Precio' },
        { key: 'total', variant: 'success' },
      ],

      anulated: false,
      showPrint: false,

      commentText: '',
      disabledAction: 'enabled',

      invoiceNumber: '',
      invoiceItems: [],

      selectedImage: {
        path: '',
        alt_text: '',
      },
    }
  },

  computed: {
    getCurrencySymbol() {
      if (this.customerOrderData.CurrencyInfo && this.customerOrderData.CurrencyInfo.symbol) {
        return this.customerOrderData.CurrencyInfo.symbol
      }
      return 'Q'
    },
    totalUnidades() {
      if (!this.customerOrderData.Detalle || !this.customerOrderData.Detalle.length) {
        return 0
      }
      return this.customerOrderData.Detalle.reduce((sum, item) => sum + (Number(item.cantidad) || 0), 0)
    },
  },

  mounted() {
    if (this.orderId > 0) {
      this.getCustomerOrderData()
    }
  },

  methods: {
    getCustomerOrderData() {
      axios
        .get(`customer-order/${this.orderId}`)
        .then(response => {
          this.customerOrderData.Id = response.data.data.Id
          this.customerOrderData.Observaciones = (response.data.data.Observaciones) ? response.data.data.Observaciones : '-'
          this.customerOrderData.Detalle = response.data.data.Detalle
          this.customerOrderData.SummaryInfo = response.data.data.SummaryInfo
          this.customerOrderData.FechaIngresoSistema = response.data.data.FechaIngresoSistema
          this.customerOrderData.FechaPedidoCliente = response.data.data.FechaPedidoCliente
          this.customerOrderData.UsuarioNombre = response.data.data.UsuarioNombre
          this.customerOrderData.Estado = response.data.data.Estado
          this.customerOrderData.Comentarios = response.data.data.Comentarios
          this.customerOrderData.Facturas = response.data.data.Facturas
          this.customerOrderData.Observaciones = response.data.data.Observaciones
          this.customerOrderData.Vendedor = response.data.data.UsuarioNombre
          this.customerOrderData.VendedorEmail = response.data.data.VendedorEmail
          this.customerOrderData.Cotizacion = response.data.data.Cotizacion
          this.customerOrderData.VentaCaida = response.data.data.VentaCaida || null
          // Info del cliente
          this.customerOrderData.CustomerInfo = response.data.data.CustomerInfo
          this.customerOrderData.DireccionEnvio = response.data.data.DireccionEnvio
          this.customerOrderData.PriceListInfo = response.data.data.PriceListInfo
          this.customerOrderData.CurrencyInfo = response.data.data.CurrencyInfo

          if (response.data.data.Estado === 'Cancelado') {
            this.anulated = true
          }
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    getCurrentDateTime() {
      const date = new Date()
      const dateStr = `${(`00${date.getMonth() + 1}`).slice(-2)}/${
        (`00${date.getDate()}`).slice(-2)}/${
        date.getFullYear()} ${
        (`00${date.getHours()}`).slice(-2)}:${
        (`00${date.getMinutes()}`).slice(-2)}:${
        (`00${date.getSeconds()}`).slice(-2)}`
      return dateStr
    },

    getStatusVariant(estado) {
      switch (estado) {
        case 'Pendiente': return 'info'
        case 'Autorizado': return 'success'
        case 'Correcciones': return 'warning'
        case 'Rechazado': return 'danger'
        case 'Anulado': return 'danger'
        case 'Despachado': return 'secondary'
        case 'Facturado': return 'dark'
        default: return 'secondary'
      }
    },

    getEditButtonTooltip(estado) {
      switch (estado) {
        case 'Pendiente': return 'Editar pedido en proceso'
        case 'Correcciones': return 'Realizar correcciones solicitadas'
        default: return 'Editar pedido'
      }
    },

    changeStatus(estado) {
      let alertMsg = ''
      let successMsg = ''

      switch (estado) {
        case 'Autorizado':
          alertMsg = '¿Está seguro de autorizar el pedido?'
          successMsg = 'Pedido autorizado correctamente.'
          break
        case 'Despachado':
          alertMsg = '¿Está seguro de marcar el pedido como despachado?'
          successMsg = 'Pedido marcado como despachado correctamente.'
          break
        case 'Correcciones':
          alertMsg = '¿Está seguro de solicitar correcciones en el pedido?'
          successMsg = 'Correcciones solicitadas correctamente.'
          break
        case 'Rechazado':
          alertMsg = '¿Está seguro de rechazar el pedido?'
          successMsg = 'Pedido rechazado correctamente.'
          break
        case 'Anulado':
          alertMsg = '¿Está seguro de anular el pedido?'
          successMsg = 'Pedido anulado correctamente.'
          break
        case 'Facturado':
          this.$bvModal.show('factura-modal')
          return // Salir del método para evitar ejecutar el resto del código
        default:
          alertMsg = '¿Está seguro de realizar esta acción?'
          successMsg = 'Acción realizada correctamente.'
          break
      }

      this.$bvModal
        .msgBoxConfirm(alertMsg, {
          title: 'Atención',
          size: 'sm',
          okVariant: 'primary',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          okTitleVariant: 'outline-danger',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .put(`customer-order/${this.orderId}/status/${estado}`)
              .then(response => {
                if (response.data.res) {
                  this.makeToast('success', 'Actualizado', successMsg)
                  this.getCustomerOrderData()
                } else {
                  throw (response)
                }
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    saveInvoices() {
      if (this.invoiceItems.length === 0) {
        this.makeToast('warning', 'Alerta', 'Debe ingresar al menos una factura.')
        return
      }

      const invoices = {
        idpedidos_enc: this.orderId,
        facturas: this.invoiceItems,
      }

      axios
        .post('customer-order-invoices', invoices)
        .then(response => {
          if (response.data.res) {
            this.makeToast('success', response.data.msg, response.data.data)
            this.getCustomerOrderData()
            this.$bvModal.hide('factura-modal')
            this.invoiceItems = []
          } else {
            throw (response)
          }
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    editInvoices() {
      this.$bvModal.show('factura-modal')
      this.invoiceItems = this.customerOrderData.Facturas.map(item => item.factura)
    },

    addInvoiceItem(item) {
      if (item === '') return
      if (this.invoiceItems.includes(item)) {
        this.makeToast('warning', 'Alerta', 'Esta factura ya fue ingresada.')
        return
      }
      this.invoiceItems.push(item)
      this.invoiceNumber = ''
    },

    userData() {
      return JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },

    addComment() {
      this.disabledAction = 'disabled'
      if (this.commentText.length > 0) {
        const comment = { comment: this.commentText }
        axios
          .post(`comments/order/${this.orderId}`, comment)
          .then(response => {
            if (response.data.res) {
              this.makeToast('success', 'Comentario agregado', 'Comentario agregado correctamente.')
              this.commentText = ''
              this.disabledAction = 'enabled'
              this.getCustomerOrderData()
            } else {
              throw (response)
            }
          })
          .catch(error => {
            this.showErrors(error)
          })
      }
    },

    getImageUrl(path) {
      if (!path) return NO_IMAGE_URL
      return buildStorageUrl(path)
    },

    handleImageError(event) {
      const img = event && event.target
      if (img) img.setAttribute('src', NO_IMAGE_URL)
    },

    async generarPDF() {
      try {
        // Verificar que el pedido esté autorizado
        if (!['Autorizado', 'Despachado', 'Facturado'].includes(this.customerOrderData.Estado)) {
          this.makeToast('warning', 'Advertencia', 'Solo se puede generar PDF de pedidos autorizados.')
          return
        }

        // Preparar datos del pedido para el generador PDF
        const orderDataForPDF = {
          ...this.customerOrderData,
          // Agregar campos adicionales que necesita el generador
          VendedorEmail: this.customerOrderData.VendedorEmail || '',
        }

        const generator = new OrderGenerator(orderDataForPDF)
        const blob = await generator.generate()

        // Crear un enlace de descarga
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `PedidoDutton_${this.customerOrderData.Id}.pdf`
        link.click()

        this.makeToast('success', 'PDF Generado', 'El PDF del pedido se ha descargado exitosamente.')
      } catch (error) {
        console.error('Error al generar PDF:', error)
        this.makeToast('danger', 'Error', 'No se pudo generar el PDF del pedido.')
      }
    },

    showErrors(error) {
      const message = error?.response?.data?.message || error?.message || 'Ha ocurrido un error inesperado'
      this.makeToast('danger', 'Error', message)
    },
  },
}
</script>

<style lang="scss">

td,
th,
tr,
table {
    border-top: 1px solid black;
    border-collapse: collapse;
}

td.producto,
th.producto {
    width: 145px;
    max-width: 145px;
}

td.cantidad,
th.cantidad {
    width: 110px;
    max-width: 110px;
    word-break: break-all;
}

td.precio,
th.precio {
    width: 70px;
    max-width: 70px;
    word-break: break-all;
}

.centrado {
    text-align: center;
    align-content: center;
}

.ticket {
    width: 240px;
    max-width: 240px;
    font-size: 10px;
}

img {
    max-width: inherit;
    width: inherit;
}

.total_size {
  font-size:15px
}

.status-flow-diagram {
  .status-row {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 10px 0;

    .status-box {
      padding: 10px 15px;
      border-radius: 5px;
      text-align: center;
      font-weight: bold;
      min-width: 120px;

      &.info {
        background-color: #00ccff1a;
        color: #00ccff;
      }

      &.secondary {
        background-color: #82868b1a;
        color: #82868b;
      }

      &.success {
        background-color: #28c76f1a;
        color: #28c76f;
      }

      &.warning {
        background-color: #ff9f431a;
        color: #ff9f43;
      }

      &.danger {
        background-color: #ea54551a;
        color: #ea5455;
      }

      &.dark {
        background-color: #4b4b4b1a;
        color: #4b4b4b;
      }

      small {
        font-size: 0.75rem;
        font-weight: normal;
      }
    }

    .arrow {
      margin: 0 15px;
      color: #82868b;
      font-weight: bold;
    }
  }
}
</style>
